import { type ChangeEvent, useState } from "react";

import {
  Button,
  Card,
  Heading,
  Icon,
  Modal,
  type ModalProps,
  Tag,
  Textarea,
  addToast,
} from "@icg360/design-system";
import type { UserPoliciesList } from "@keystone-ui/types";

import { Stack } from "components/common/stack";
import { usePoliciesList, usePolicyId } from "data/ss-data-hooks";
import { formatAddress } from "utils";

import styles from "./policy-notes.module.scss";

const NOTE_STORAGE_KEY = "policy-notes";

type PolicyNotesProps = {
  onClose: ModalProps["onClose"];
};

export const PolicyNotes = ({ onClose }: PolicyNotesProps) => {
  const { data: policies } = usePoliciesList();
  const selectedPolicyId = usePolicyId();
  const [editingPolicyId, setEditingPolicyId] = useState<string>();
  const [editingNoteText, setEditingNoteText] = useState<string>();

  const notes = JSON.parse(localStorage.getItem(NOTE_STORAGE_KEY) ?? "{}");

  const onEdit = (insightPolicyId: string) => {
    setEditingPolicyId(insightPolicyId);
    setEditingNoteText(notes[insightPolicyId] ?? "");
  };

  const onChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.currentTarget.value;
    setEditingNoteText(val);
  };
  const onSave = () => {
    if (!editingPolicyId) {
      return;
    }
    notes[editingPolicyId] = editingNoteText;
    localStorage.setItem(NOTE_STORAGE_KEY, JSON.stringify(notes));
    setEditingPolicyId(undefined);
    addToast("Note saved", { icon: true });
  };

  const onViewPolicy = (policy: UserPoliciesList[number]) => {
    const policyUrl = new URL(window.location.href);
    policyUrl.searchParams.set("policyId", policy.keystonePolicyId);
    window.location.assign(policyUrl);
  };

  const copyPolicyIdToClipboard = (policyId) => {
    navigator.clipboard.writeText(policyId);
    addToast("Policy # copied to clipboard", { icon: true });
  };

  return (
    <Modal heading="Policy notes" onClose={onClose} open noFooter>
      <Stack>
        {policies?.map((policy) => {
          const isActive = RegExp(/(^active)/gi).test(policy.policyState ?? "");
          return (
            <Card key={policy.keystonePolicyId} border>
              <div className={styles.policyItemHeader}>
                <Stack gap="md" className={styles.policyIcons}>
                  <Icon
                    name={policy.isCommercial ? "Store" : "House"}
                    size="lg"
                  />
                  <div
                    title={policy?.policyState ?? ""}
                    className={
                      isActive ? styles.activePolicy : styles.cancelledPolicy
                    }
                  >
                    <Icon
                      name={isActive ? "Success" : "Ineligible"}
                      size="lg"
                    />
                  </div>
                </Stack>
                <div className={styles.content}>
                  <Heading size="xs">
                    {formatAddress(policy.propertyAddress, true)}
                  </Heading>
                  <div className={styles.idTagWrapper}>
                    <button
                      onClick={() =>
                        copyPolicyIdToClipboard(policy.platformPolicyId)
                      }
                      data-testid={`copy-policy-id-btn-${policy.keystonePolicyId}`}
                    >
                      <Tag
                        className={styles.policyTag}
                        bgColor="accentTealLight"
                        textColor="accentTealDarkest"
                      >
                        {policy.platformPolicyId}
                      </Tag>
                    </button>
                  </div>
                  {policy.keystonePolicyId === editingPolicyId ? (
                    <>
                      <Textarea value={editingNoteText} onChange={onChange} />
                      <Button
                        size="xs"
                        onPress={() => onSave()}
                        data-testid={`save-note-btn-${policy.keystonePolicyId}`}
                      >
                        Save
                      </Button>
                    </>
                  ) : (
                    <>
                      {notes[policy.keystonePolicyId] ? (
                        <div className={styles.note}>
                          {notes[policy.keystonePolicyId] ?? ""}
                        </div>
                      ) : null}
                      <div className={styles.bottomButtonWrapper}>
                        <Button
                          appearance="tertiary"
                          icon="Edit"
                          size="xs"
                          onPress={() => onEdit(policy.keystonePolicyId)}
                          data-testid={`edit-note-btn-${policy.keystonePolicyId}`}
                        />
                        {policy.keystonePolicyId === selectedPolicyId ? (
                          <Tag
                            size="comfortable"
                            bgColor="accentPurpleLight"
                            textColor="accentPurpleDarkest"
                          >
                            Current policy
                          </Tag>
                        ) : (
                          <Button
                            appearance="secondary"
                            size="xs"
                            onPress={() => onViewPolicy(policy)}
                            data-testid={`view-policy-btn-${policy.keystonePolicyId}`}
                          >
                            View policy
                          </Button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </Stack>
    </Modal>
  );
};
